<template>
  <div data-dusk="main-table" class="responsive">
    <dd-custom-table :class="{'rounded': rounded}">
      <dd-table-header
          @toggle-all="toggleAllRows"
          @on-order-by="onOrderBy"
          :loadPageObject="loadPageObject"
          :allValuesAreSelected="allValuesAreSelected"
          :hasActionButtons="hasActionButtons"
          :headers="headers"
          :hasSelect="hasSelect"
          :actionWidth="actionWidth"
          :singleSelect="singleSelect"
      ></dd-table-header>
      <tbody data-dusk="table-body" v-if="!loading && rows">
        <dd-table-row
            :selected-from-row="selectedFromRow"
            @toggle-row="toggleRow"
            v-for="row in rows"
            :key="row[selectKey]"
            :row="row"
            :headers="headers"
            :selectedRows="selectedRows"
            :selectKey="selectKey"
            :hasSelect="hasSelect"
            :singleSelect="singleSelect"
            :entire-row-selects="entireRowSelects"
        >
          <template #actions>
            <slot v-bind:row="row" name="actions"></slot>
          </template>
        </dd-table-row>
      </tbody>
      <dd-skeleton v-if="loadingAdditional || loading" :rows="loadingCount" :columns="totalColumns"></dd-skeleton>
    </dd-custom-table>

    <dd-pagination
        v-if="paginator"
        @per-page="onPerPageChange"
        @go-to-page="onPaginationChange"
        :paginator="paginator"
        :showPerPage="showPerPage"
    />
  </div>
</template>

<script>
import DdTableRow from "./TableRow";
import DdTableHeader from "./TableHeader";
import DdPagination from "../pagination/Pagination.vue";
import SkeletonLoader from "../../modules/static_search/selection/components/SkeletonLoader.vue";

export default {
  name: "MainTable",
  components: {
    DdTableRow, DdTableHeader, DdPagination, SkeletonLoader
  },
  emits: ['load-page', 'update-selected', 'update-deselected'],
  computed: {
    hasActionButtons() {
      return this.$scopedSlots.actions !== undefined;
    },
    totalColumns() {
      return this.headers.length + (this.hasSelect ? 1 : 0) + (this.hasActionButtons ? 1: 0 );
    },
    allValuesAreSelected() {
      if (!this.rows) return false;
      return this.selectedRows.length === this.rows.length;
    }
  },
  data() {
    return {
      loadPageObject: this.initialLoadPageObject,
      keyedRows: [],
      selectedRows: [],
      deselectedRows: [],
    }
  },
  props: {
    loadingAdditional: {
      type: Boolean,
      required: false,
      default: false,
    },
    selected: {
      type: Array,
      required: false,
      default: () => [],
    },
    loadingCount: {
      default: 5,
      required: false,
      type: Number,
    },
    actionWidth: {
      required: false,
      type: String,
      default: '60px',
    },
    hasSelect: {
      required: false,
      type: Boolean,
      default: true,
    },
    singleSelect: {
      required: false,
      type: Boolean,
      default: false,
    },
    entireRowSelects: {
      required: false,
      type: Boolean,
      default: false,
    },
    loading: {
      required: false,
      type: Boolean,
      default: false,
    },
    loaded: {
      required: false,
      type: Boolean,
      default: true,
    },
    selectKey: {
      type: String,
      required: false,
      default: 'id',
    },
    initialLoadPageObject: {
      type: Object,
      required: false,
      default: () => {
        return {};
      },
    },
    paginator: {
      type: Object,
      required: false,
    },
    showPerPage:{
      type: Boolean,
      required: false,
      default: true
    },
    headers: {
      required: true,
    },
    rows: {
      type: Array,
      required: false,
    },
    rounded: {
      type: Boolean,
      required: false,
      default: false,
    },
    selectedFromRow: {
      type: Boolean,
      default: false,
      required: false,
    },
    selectReverse: {
      type: Boolean,
      default: false,
      required: false,
    },
    keepSelectedAfterPageChange: {
      type: Boolean
    }
  },
  mounted(){
    if(this.selected.length) {
      this.selectedRows = this.selected;
    }
    if(this.selectReverse) {
      this.selectAll();
    }
  },
  watch: {
    selected() {
      this.selectedRows = this.selected;
    },
    loadPageObject() {
      this.loadPage();
    },
    rows() {
      if (this.selectReverse) {
        this.selectAll();
      } else if (!this.keepSelectedAfterPageChange) {
        this.selectedRows = [];
        this.newSelectedRows();
      }
    }
  },
  methods: {
    selectAll() {
      // Select all except skip explicitly deselected rows.
      if (this.selectedFromRow) {
        this.selectedRows = this.rows.filter((item) => {
          return !this.deselectedRows.includes(item);
        });
      } else {
        this.selectedRows = this.rows.map((item) => {
          return item[this.selectKey];
        }).filter((item) => {
          return !this.deselectedRows.includes(item);
        })
      }

      this.newSelectedRows();
    },
    toggleAllRows() {
      if (this.selectedRows.length === this.rows.length) {
        this.deselectedRows = [...this.deselectedRows, ...this.selectedRows];
        this.selectedRows = [];
      } else {
        if (this.selectedFromRow) {
          this.selectedRows = this.rows;
          this.deselectedRows = this.deselectedRows.filter((deselected) => {
            return deselected === this.rows.map((row) => row[this.selectKey])
          });
        } else {
          this.selectedRows = this.rows.map((item) => item[this.selectKey]);
          this.deselectedRows = this.deselectedRows.filter((item) => !this.selectedRows.includes(item));
        }
      }

      this.newSelectedRows();
    },
    toggleRow(row) {
      if (this.singleSelect){
        if (this.selectedRows[0] === row[this.selectKey]){
          this.selectedRows = [];
        } else {
          this.selectedRows = [row[this.selectKey]];
        }
      } else {
        let existsAtIndex;

        if (this.selectedFromRow) {
          existsAtIndex = this.selectedRows.findIndex((selectedRow) => selectedRow[this.selectKey] === row[this.selectKey]);
        } else {
          existsAtIndex = this.selectedRows.findIndex((selectedKeyValue) => selectedKeyValue === row[this.selectKey]);
        }

        // If deselected row remove it from selected rows.
        if (existsAtIndex !== -1) {
          this.selectedRows.splice(existsAtIndex, 1);
          if(this.selectedFromRow) {
            this.deselectedRows.push(row);
          } else {
            this.deselectedRows.push(row[this.selectKey]);
          }
        } else { // Else add it to selected rows.
          if (this.selectedFromRow) {
            existsAtIndex = this.deselectedRows.findIndex((selectedRow) => selectedRow[this.selectKey] === row[this.selectKey]);
          } else {
            existsAtIndex = this.deselectedRows.findIndex((selectedKeyValue) => selectedKeyValue === row[this.selectKey]);
          }

          this.deselectedRows.splice(existsAtIndex, 1);

          if (this.selectedFromRow) {
            this.selectedRows.push(row);
          } else {
            this.selectedRows.push(row[this.selectKey]);
          }
        }
      }

      this.newSelectedRows();
    },
    newSelectedRows() {
      this.$emit('update-selected', (this.singleSelect ? this.selectedRows[0] : this.selectedRows));
      this.$emit('update-deselected', this.deselectedRows);
    },
    onOrderBy(order, orderBy) {
      this.loadPageObject = {...this.loadPageObject, orderBy, order};
    },
    onPerPageChange(perPage) {
      let page = 1;

      if (Object.keys(this.loadPageObject).length > 0 && this.loadPageObject.total_pages) {
        let newTotalPages = Math.ceil(this.loadPageObject.total_pages / perPage)

        page = this.loadPageObject.current_page > newTotalPages ? 1 : this.loadPageObject.current_page;
      }

      this.loadPageObject = {...this.loadPageObject, page, perPage};
    },
    onPaginationChange(page) {
      this.loadPageObject = {...this.loadPageObject, page}

      if (!this.keepSelectedAfterPageChange) {
        this.selectedRows = [];
        this.newSelectedRows();
      }
    },
    loadPage() {
      this.$emit('load-page', this.loadPageObject);
    },
  }
}
</script>

<style lang="scss" scoped>
.responsive {
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  overflow-x: auto;
}
</style>
